import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatBDT } from '@maktech/shared';
import { api, errorMessage } from '../../api/client';
import { Modal } from '../../components/Modal';
import { useAuth } from '../auth/AuthContext';
import { useCart } from './CartContext';

/**
 * Book delivery + cash on delivery — placeBookOrder() (:5071).
 *
 * PROTOTYPE BUG NOT REPRODUCED: placeBookOrder ran `arr.length=0` and THEN
 * built its confirmation with bookTotal()+60 — reading an already-emptied cart,
 * so every confirmation told the customer "Cash on delivery: ৳60" no matter
 * what they ordered. The order row was right; only the message the human read
 * was wrong. Here the amount comes from the server's response, so the number
 * confirmed is the number recorded.
 */
export function ShipModal({ onClose }: { onClose: () => void }) {
  const { books, clearBooks } = useCart();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState<{ orderId: string; total: number } | null>(null);

  const { data } = useQuery({
    queryKey: ['books'],
    queryFn: async () => (await api.get<{ books: { slug: string; price: number }[] }>('/books')).data,
  });

  const subtotal = books.reduce((s, l) => {
    const b = data?.books.find((x) => x.slug === l.slug);
    return s + (b ? b.price * l.qty : 0);
  }, 0);
  const total = subtotal + 6000;

  async function place() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ orderId: string; total: number }>('/book-orders', {
        items: books,
        name,
        phone,
        address,
      });
      clearBooks();
      await qc.invalidateQueries({ queryKey: ['books'] }); // stock moved
      setPlaced({ orderId: r.data.orderId, total: r.data.total });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (placed) {
    return (
      <Modal onClose={onClose} label="Order confirmed">
        <div className="empty" data-testid="ship-done" style={{ padding: '24px 10px' }}>
          <div className="big">📦</div>
          <b>Order placed — {placed.orderId}</b>
          <p>
            Your books are being packed. Delivery takes 2–4 working days inside Dhaka, 4–7 outside.
            <br />
            {/* The REAL amount, from the server. */}
            Cash on delivery: <b data-testid="ship-cod-total">{formatBDT(placed.total)}</b>.
          </p>
          <button className="btn btn-primary btn-sm" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} label="Delivery details">
      <h3 style={{ marginBottom: 4 }}>Delivery details</h3>
      <p className="asub" style={{ margin: '0 0 12px' }}>
        Cash on delivery — pay the courier when your books arrive.
      </p>

      <div className="field">
        <label htmlFor="shName">Full name</label>
        <input id="shName" data-testid="ship-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="shPhone">Phone</label>
        <input id="shPhone" data-testid="ship-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="shAddr">Full address</label>
        <textarea id="shAddr" data-testid="ship-address" rows={3} value={address} onChange={(e) => setAddress(e.target.value)} required />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, margin: '10px 0' }}>
        <span>Total (incl. {formatBDT(6000)} delivery)</span>
        <span id="shipTotal" data-testid="ship-total">
          {formatBDT(total)}
        </span>
      </div>

      {error ? (
        <p data-testid="ship-error" style={{ color: 'var(--coral)', fontSize: 13.5 }}>
          {error}
        </p>
      ) : null}

      <button className="btn btn-primary btn-block" data-testid="ship-place" disabled={busy} onClick={() => void place()}>
        {busy ? 'Placing…' : 'Place order (cash on delivery)'}
      </button>
    </Modal>
  );
}
